import { routeConstants, codetableBreadcrumbs } from '../../routes/route-constants';
import * as routeUrls from './route-urls';
import { toAbsoluteUrl, removeHostingPath } from 'app/infrastructure/hosting';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { getResolver } from 'app/area/shared/codetable/get-resolver';
import { NavigateFunction, NavigateOptions, To, useLocation, Location } from 'react-router-dom';
import { QueryObject } from 'app/components/enhancers/with-router';
import { getPathPrefix, inStartupDashboard, removeCaseDraftSearchParams } from 'app/components/helpers';

let navigateFunction: NavigateFunction;
let queryObject: QueryObject;
let currentLocation: Location;

export const queryFromSearchParams = (searchString: string) => {
  const searchParams = new URLSearchParams(searchString);
  const entries = [...searchParams.entries()];

  return entries.reduce((acc, [key, value]) => {
    if (acc.hasOwnProperty(key)) {
      const values = Array.isArray(acc[key]) ? acc[key] : [acc[key]];
      return { ...acc, [key]: [...values, value] };
    }
    return { ...acc, [key]: value };
  }, {});
};

export const caseDraftParametersFromSearchParams = (searchParams: URLSearchParams) => {
  return searchParams.getAll('d').map(d => {
      const [domainId, rawValue] = d.split(';');
      let value;      

      if (rawValue?.includes(',')) {
        // if Array (numbers OR strings)
        value = rawValue.split(',').map(v =>
          isNaN(Number(v)) ? v : Number(v)
        );
      } else if (isNaN(Number(rawValue))) {// if String
        value = rawValue;
      } else {
        value = Number(rawValue); // if Number
      }

      return {
        domainId: Number(domainId),
        value: value
      };
    });
};

export function mergeQueryFromLocation(
  currentSearch: string,
  queryParams?: QueryObject,
  clearQuery: boolean = false
): Record<string, string> {  
  const currentQuery = Object.fromEntries(
    new URLSearchParams(currentSearch).entries()
  );

  //merge with queryParams if clearQuery is false
  const merged = clearQuery
    ? queryParams ?? {}
    : { ...currentQuery, ...(queryParams ?? {}) };

  //remove unwanted keys
  const { addLossPotential, addResidual, section, ...query } = merged;

  //safely encode everything
  const safeQuery: Record<string, string> = {};
  Object.entries(query).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      // keep null/undefined as it is
      safeQuery[key] = value;
  } else {
      // trim only leading/trailing ?, & or spaces
      const str = String(value).replace(/^[?&\s]+|[?&\s]+$/g, '');
      safeQuery[key] = encodeURIComponent(str);
    }
  });

  return safeQuery;
}

/**
 * Store location and navigate function from hooks used in with-router enhancer.
 * Query object is extracted from search parameter string
 */
export function useRouterProps({ location, navigate }: { location: Location; navigate: NavigateFunction }) {
  navigateFunction = navigateFunction ?? navigate;
  currentLocation = location;
  queryObject = queryFromSearchParams(location?.search);
}

/**
 * Hook that allows to use query params as object i.e. query.sectionId
 * useSearchParams returns URLSearchParams type which requires using search.get(sectionId)
 */
export function useQueryParams(): { query: QueryObject } {
  const location = useLocation();
  return { query: queryFromSearchParams(location.search) };
}

export function toStartupView(replaceCurrent: boolean = false) {
  replaceCurrent ? replace(routeConstants.startupView) : to(routeConstants.startupView);
}

export function toCaseView(caseNumber: number, replaceCurrent: boolean = false, newWindow: boolean = false) {
  const url = routeUrls.toCaseView(caseNumber);
  replaceCurrent ? replace(url) : (newWindow ? window.open(toAbsoluteUrl(url)) : to(url));
}

export function toCaseViewInternal(caseNumber: number) {
  const url = routeUrls.toCaseView(caseNumber);
  replace({ pathname: url }, { state: { internal: true } });
}

export function toCaseEdit(caseNumber: number, entityPath: string = '') {
  const l = currentLocation;
  to({ pathname: routeUrls.toCaseEdit(caseNumber, entityPath), search: l.search });
}

export function toCaseEditInModal(caseNumber: number, entityPath: string = '') {
  const l = currentLocation;
  const basePath = l.pathname.split('/case')[0];
  to({ pathname: routeUrls.toCaseEditInModal(basePath, caseNumber, entityPath), search: l.search });
}

export function toCaseEditSection(caseNumber: number, section: number, query: string = '') {
  to({ pathname: routeUrls.toDraft(caseNumber, ''), search: `?section=${section}` + query });
}

export function toCaseEditSectionInModal(caseNumber: number, section: number, query: string = '') {
  const l = currentLocation;
  const basePath = l.pathname.split('/case')[0];
  const pathPrefix = getPathPrefix() || '';
  const prefix = basePath === pathPrefix ? '' : pathPrefix;
  to({ pathname: prefix + basePath + routeUrls.toDraftInModal(caseNumber, ''), 
    search: `?section=${section}` + query + `${l.search.replace(/^\?/, '&')}` });
}

export function toCaseAddLossPotentialEvaluation(caseNumber: number, section: number) {
  toCaseEditSection(caseNumber, section, `&addLossPotential=true`);
}

export function toCaseAddLossPotentialEvaluationInModal(caseNumber: number, section: number) {
  toCaseEditSectionInModal(caseNumber, section, `&addLossPotential=true`);
}

export function toCaseAddRiskEvaluations(caseNumber: number, section: number) {
  toCaseEditSection(caseNumber, section, `&addLossPotential=true&addResidual=true`);
}

export function toCaseAddRiskEvaluationsInModal(caseNumber: number, section: number) {
  toCaseEditSectionInModal(caseNumber, section, `&addLossPotential=true&addResidual=true`);
}

export function toCaseViewEvaluation(caseNumber: number, entityPath: string = '', sectionNumber: number = undefined) {
  const url = routeUrls.toCaseViewEvaluation(caseNumber, entityPath, sectionNumber);
  to(url);
}

export function toCaseDuplicate(caseNumber: number) {
  to(routeUrls.toCaseDuplicate(caseNumber));
}

export function toCaseDuplicateInModal(caseNumber: number, query: string = '') {
  const l = currentLocation;
  const basePath = l.pathname.split('/case')[0];
  to({ pathname: basePath + routeUrls.toCaseDuplicate(caseNumber), 
    search: query + `${l.search.replace(/^\?/, '&')}` });
}

export function toParentPathFromCaseViewModal() {  
  const parent = location.pathname.split("/case")[0];
  const currentSearch = location.search;
  if(currentSearch) {
    replace({ pathname: parent, search: currentSearch });
    removeCaseDraftSearchParams(new URLSearchParams(currentSearch));
  } else {
    replace(parent);
  }     
} 

export function toCodeSelectView(breadcrumbs: number[]) {
  to(routeUrls.toCodeSelectView(codetableBreadcrumbs(breadcrumbs)));
}

export function toCodeList(rootId: number, optionsCollectionId?: number, categoryId?: number) {
  const url = `/administration/optionsCollection/${rootId ? rootId + '/' : ''}${isDefinedIncludingZero(optionsCollectionId)
    ? optionsCollectionId : ''}`;
  window.open(toAbsoluteUrl(categoryId ? url + `?categoryId=${categoryId}` : url));
}

export function toCodeListWithBreadcrumbs(breadcrumbs: number[], categoryId?: number) {

  const resolver = getResolver(breadcrumbs);
  const url = resolver?.(breadcrumbs) ??
    '/administration/optionsCollection' + codetableBreadcrumbs(breadcrumbs);

  window.open(toAbsoluteUrl(categoryId ? url + `?categoryId=${categoryId}` : url));
}

export function toCreateCase(caseTypeId: number) {
  replace(routeUrls.toCreateCase(caseTypeId));
}

export function toPrintCaseView(caseUrl: string) {
  const url = (caseUrl || toAbsoluteUrl(currentLocation.pathname)) + stringifyQuery({ ...queryObject, expandAll: 'true', print: 'true' });
  if (window) {
    window.open(url,
      'Print window',
      'menubar=no,location=no,resizable=no,scrollbars=no,status=no,width=800,height=800');
  }
}

/**
 * Redirect to a draft, with a optional query string parameter
 *
 * @param draftId
 * @param queryParams passing undefined preserces the current client `location.search` as query string
 * @param entityPath
 */
export function toDraft(draftId: number, queryParams?: QueryObject, entityPath: string = '') {
  const query = queryParams || queryObject;
  replace({ pathname: routeUrls.toDraft(draftId, entityPath), search: stringifyQuery(query) });
}

export function toDraftCaseViewModal(draftId: number, queryParams?: QueryObject, 
  entityPath: string = '', clearQuery: boolean = false) {
  const query = clearQuery ? undefined : queryParams || queryObject;
  const { pathname: currentPath } = currentLocation;  
  const splittedPath = currentPath.split('/');
  // Get the draft ID after "favourite/draft"
  const favouriteDraftIndex = splittedPath.findIndex((seg, i, arr) => arr[i - 1] === 'favourite' && seg === 'draft');
  const favouriteDraftId = favouriteDraftIndex !== -1 ? Number(splittedPath[favouriteDraftIndex + 1]) : undefined;  

  // Get the dashboard type after "dashboard"
  const dashboardIndex = splittedPath.findIndex(seg => seg === "dashboard");
  const dashboardType = dashboardIndex !== -1 ? Number(splittedPath[dashboardIndex + 1]) : undefined;

  // Get the bowtie params"
  const bowtieIndex = splittedPath.findIndex(seg => seg === "bowtie");
  const bowtieViewCaseNumber = bowtieIndex !== -1 ? Number(splittedPath[bowtieIndex + 1]) : undefined;

  if (currentPath.includes('/bowtie/')) {
    replace({ pathname: routeUrls.toDraftCaseViewModalBowtie(bowtieViewCaseNumber, draftId, entityPath), 
      search: stringifyQuery({ ...query }) });
    return;
  }

  if (currentPath.includes('/dashboard/') && !!dashboardType) {
    replace({ pathname: routeUrls.toDraftCaseViewModalDashboard(dashboardType, favouriteDraftId, draftId, entityPath), 
      search: stringifyQuery({ ...query }) });
    return;
  }
  if (inStartupDashboard()) {
    replace({ pathname: routeUrls.toDraftCaseViewModalStartupDashboard(draftId, entityPath), 
      search: stringifyQuery({ ...query }) });
    return;
  }
  replace({ pathname: routeUrls.toDraftCaseViewModalCaseListing(favouriteDraftId, draftId, entityPath), 
    search: stringifyQuery({ ...query }) });
}

export function toCaseViewModal(caseId: number, queryParams?: QueryObject, entityPath: string = '', clearQuery: boolean = false) {
  
  const query = mergeQueryFromLocation(currentLocation.search, queryParams, clearQuery);

  const { pathname: currentPath } = currentLocation;  
  const splittedPath = currentPath.split('/');
  // Get the draft ID after "favourite/draft"
  const favouriteDraftIndex = splittedPath.findIndex((seg, i, arr) => arr[i - 1] === 'favourite' && seg === 'draft');
  const favouriteDraftId = favouriteDraftIndex !== -1 ? Number(splittedPath[favouriteDraftIndex + 1]) : undefined;  

  // Get the dashboard type after "dashboard"
  const dashboardIndex = splittedPath.findIndex(seg => seg === "dashboard");
  const dashboardType = dashboardIndex !== -1 ? Number(splittedPath[dashboardIndex + 1]) : undefined;

  // Get the bowtie params"
  const bowtieIndex = splittedPath.findIndex(seg => seg === "bowtie");
  const bowtieViewCaseNumber = bowtieIndex !== -1 ? Number(splittedPath[bowtieIndex + 1]) : undefined;

  if (currentPath.includes('/bowtie/')) {
    replace({ pathname: routeUrls.toCaseViewModalBowtie(bowtieViewCaseNumber, caseId), 
      search: stringifyQuery({ ...query }) });
    return;
  }

  if (currentPath.includes('/dashboard/') && !!dashboardType) {
    replace({ pathname: routeUrls.toCaseViewModalDashboard(dashboardType, favouriteDraftId, caseId), 
      search: stringifyQuery({ ...query }) });
    return;
  }
  if (inStartupDashboard()) {
    replace({ pathname: routeUrls.toCaseViewModalStartupDashboard(caseId), 
      search: stringifyQuery({ ...query }) });
    return;
  }
  replace({ pathname: routeUrls.toCaseViewModalCaseListing(favouriteDraftId, caseId), search: stringifyQuery({ ...query }) });
}

export function toCaseListing(
  favouriteId: number, report?: number, openFilters?: boolean,
  editSavedFavourite?: boolean, replaceUrl?: boolean
) {
  const route = replaceUrl ? replace as NavigateFunction : to as NavigateFunction;
  route({
    pathname: routeUrls.toCaseListing(favouriteId),
    search: stringifyQuery({
      ...(report ? { report: String(report) } : {}),
      ...(openFilters ? { openFilters: 'true' } : {}),
      ...(editSavedFavourite ? { editSavedFavourite: 'true' } : {})
    })
  });
}

export function toUpdateSavedReport(favouriteId: number, newWindow: boolean = false) {
  newWindow
    ? window.open(toAbsoluteUrl(routeUrls.toUpdateSavedReport(favouriteId)))
    : to(routeUrls.toUpdateSavedReport(favouriteId));
}

export function toCompanyReportDraftFavourite(
  favouriteId: number, companyReportId: number, editSavedFavourite?: boolean, replaceUrl?: boolean, queryParams?: string) {
  const route = replaceUrl ? replace : to;
  route({
    pathname: routeUrls.toCompanyReportDraftFavourite(favouriteId, companyReportId, queryParams),
    search: stringifyQuery({
      ...(editSavedFavourite ? { editSavedFavourite: 'true' } : {})
    })
  });
}

export function toUpdateSavedCompanyReport(favouriteId: number, companyReportId: number) {
  replace({
    pathname: routeUrls.toUpdateSavedCompanyReport(favouriteId, companyReportId)
  });
}

export function toSavedCompanyReport(favouriteId: number, companyReportId: number, queryParams?: string) {
  replace({
    pathname: routeUrls.toSavedCompanyReport(favouriteId, companyReportId, queryParams)
  });
}

export function toSavedDashboard(favouriteId: number, dashboardType: number, queryParams?: string) {
  replace({
    pathname: routeUrls.toSavedDashboard(favouriteId, dashboardType, queryParams)
  });
}

export function toUpdateSavedDashboard(favouriteId: number, dashboardType: number) {
  replace({
    pathname: routeUrls.toUpdateSavedDashboard(favouriteId, dashboardType)
  });
}

export function toDashboardDraftFavourite(
  favouriteId: number, dashboardType: number, editSavedFavourite?: boolean, replaceUrl?: boolean) {
  const route = replaceUrl ? replace : to;
  route({
    pathname: routeUrls.toDashboardDraftFavourite(favouriteId, dashboardType),
    search: stringifyQuery({
      ...(editSavedFavourite ? { editSavedFavourite: 'true' } : {})
    })
  });
}

export function toSelectSavedReports(folderId?: number, favId?: number) {
  const { pathname } = currentLocation;
  const { folder, favouriteId, ...queryWithoutSearchParams } = queryObject;
  replace({
    pathname, search: stringifyQuery({
      ...(queryWithoutSearchParams),
      ...(folderId ? { folder: String(folderId) } : {}),
      ...(favId ? { favouriteId: queryObject.favouriteId } : {})
    })
  });
}

export function toSelectSavedFavourite(favId?: number) {
  const { pathname } = currentLocation;
  const { favouriteId, ...queryWithoutFavouriteId } = queryObject;
  replace({
    pathname, search: stringifyQuery({
      ...(queryWithoutFavouriteId),
      ...(favId ? { favouriteId: String(favId) } : {})
    })
  });
}

export function toSearchView(newWindow: boolean = false) {
  newWindow
    ? window.open(toAbsoluteUrl(routeConstants.createFavouriteDraft))
    : to(routeConstants.createFavouriteDraft);
}

export function toTextItem(textId: number, textSet: number) {
  window.open(toAbsoluteUrl(routeConstants.textConfigurationItemFor(textId, textSet)));
}

export function toSearchViewWithQuery(query: string) {
  to(routeUrls.toSearchViewWithQuery(query));
}

export function toDashboardView(dashboardType: number) {
  to(routeUrls.toDashboardView(dashboardType));
}

export function toBowtie(caseNumber: number, newWindow: boolean = false) {
  const url = `/bowtie/${caseNumber}`;
  newWindow ? window.open(toAbsoluteUrl(url)) : to(url);
}

export function toBowtieConnection(caseNumber: number, searchArg: string) {
  const search = `${searchArg}` + (queryObject?.section ? `&returnToSection=${queryObject.section}` : '');
  to({ pathname: `${routeConstants.bowtie}/${caseNumber}`, search }, { state: { internal: true } });
}

export function toAdminUsersList(internal: boolean = true) {
  to({ pathname: routeConstants.userSearchView }, { state: { internal } });
}

export function toAdminRoleList(internal: boolean = true) {
  to({ pathname: routeConstants.rolesView }, { state: { internal } });
}

export function toAdminMessagesList() {
  to({ pathname: routeConstants.administrationMessagesList }, { state: { internal: false } });
}

export function toAdminFavourite(favouriteId: number) {
  to({ pathname: routeConstants.favouriteAdminItemFor(favouriteId) }, { state: { internal: false } });
}

export function toDuplicateRole(id: number) {
  to({ pathname: routeConstants.roleDuplicateFor(id) }, { state: { internal: false } });
}

export function toDuplicateUser(id: number) {
  to({ pathname: routeConstants.userDuplicateFor(id) }, { state: { internal: false } });
}

export function toSelectConnected(basePath: string = '', searchArg: string) {
  const search = `${searchArg}&returnToSection=${queryObject.section}`;
  to({ pathname: `${basePath}${routeConstants.selectConnectedCaseType}`, search }, { state: { internal: true } });
}

export function toConnectionInitiator(route: Partial<Location>) {
  const search = route.search ? `${route.search}&section=${queryObject.returnToSection}` : `?section=${queryObject.returnToSection}`;
  to({ ...route, search });
}

export function toImportView() {
  to({ pathname: routeConstants.importView }, { state: { internal: false } });
}

export function toSelectChangeCaseType(draftId: number, parentRoute?: string, 
  favouriteId?: number, dashboardType?: number, bowtieCaseId?: number) {
  return () => to({
    pathname: `${parentRoute || ''}${routeConstants.selectChangeCaseType}`,
    search: stringifyQuery({ draftId: String(draftId), favouriteId: String(favouriteId), 
      dashboardType: String(dashboardType), bowtieCaseId: String(bowtieCaseId) })
  }, {
    state: { internal: true }
  });
}

export const toSearch = (options: { [key: string]: string }) => {
  const { pathname } = currentLocation;
  const routeArg = { pathname: pathname, search: stringifyQuery({ ...queryObject, ...options }) } as Location;
  to(routeArg);
};

export function toSectionURL(id: number | string, explicitPath?: string) {
  const { pathname: currentPath } = currentLocation;
  const fullPath = explicitPath || currentPath;

  // Split path and query manually to control ? vs &
  const [basePath, queryString] = fullPath.split("?");
  const params = new URLSearchParams(queryString || "");

  if (id !== undefined && id !== null) {
    params.set("section", String(id)); // replace or add section
  }

  const finalQuery = params.toString();
  return finalQuery ? `${basePath}?${finalQuery}` : basePath;
}

export function toSection(id: number | string, explicitPath?: string, clearQuery: boolean = true) {
  const { pathname: currentPath } = currentLocation;
  if (queryObject?.section === id) {
    return;
  }
  const pathname = explicitPath || currentPath;
  // Assume we should clear params of source view when outside path
  const query = explicitPath || clearQuery ? undefined : queryObject;
  // Only replace when navigating internally within a document
  const route: NavigateFunction = explicitPath ? to as NavigateFunction : replace as NavigateFunction;
  route({
    pathname,
    search: stringifyQuery({ ...query, section: String(id) })
  }, {
    state: { internal: !explicitPath }
  });
}

export function toAllSections() {
  const { pathname } = currentLocation;
  replace({ pathname, search: stringifyQuery({ ...queryObject, expandAll: 'true' }) }, { state: { internal: true } });
}

export function toCurrentSection() {
  const { pathname } = currentLocation;
  const { expandAll: ignored, ...other } = queryObject;
  replace({ pathname, search: stringifyQuery({ ...other }) }, { state: { internal: true } });
}

export function toSetupMultiFactorAutentication(url: string) {
  window.open(url + 'Account/TotpSetup');
}

export function toDisableMultiFactorAutentication(url: string) {
  window.open(url + 'Account/TotpDisable');
}

export function toMultiFactorAutenticationRecovery(url: string, clientId: string) {
  window.open(url + 'Account/totpadmin/recovery?clientId=' + clientId);
}

/**
 * Resets the current deep link so that we can open the first invalid section after a server validate response
 */
export function toPrepareSubmit() {
  const { pathname } = currentLocation;
  const { section = undefined, ...other } = queryObject;
  replace({ pathname, search: stringifyQuery({ ...other }) }, { state: { internal: true } });
}

/**
 * Navigates to a new resource, creating a new entry in the browser history
 *
 * @param route route as string, or a <see ref="Location"/> object
 */
export function to(route: To, options?: NavigateOptions) {
  let location = route;
  let parsedUrl: URL;

  if (typeof route === 'string') {
    parsedUrl = new URL(toAbsoluteUrl(route, true));
    options = { state: { internal: false } } as NavigateOptions;
    const relativePath = removeHostingPath(parsedUrl.pathname);
    location = { pathname: relativePath, search: parsedUrl.search, hash: parsedUrl.hash };
  } else {
    location = route;
    if (route.pathname?.includes('?')) {
      parsedUrl = new URL(toAbsoluteUrl(route.pathname, true));
      location = { pathname: parsedUrl.pathname, search: parsedUrl.search, hash: parsedUrl.hash };
    }
  }

  navigateFunction(location, options);
}

/**
 * Updates the current browser URL, without adding a new entry in the browser history.
 * This prevents the user using
 * the back button to come back to the url that *was replaced*.
 *
 * @param route
 */
export function replace(route: To, options?: NavigateOptions) {
  let location = route;
  let parsedUrl: URL;

  if (typeof route === 'string') {
    parsedUrl = new URL(toAbsoluteUrl(route, true));
    options = { state: { internal: false } } as NavigateOptions;
    const relativePath = removeHostingPath(parsedUrl.pathname);
    location = { pathname: relativePath, search: parsedUrl.search, hash: parsedUrl.hash };
  } else {
    location = route;
    if (route.pathname?.includes('?')) {
      parsedUrl = new URL(toAbsoluteUrl(route.pathname, true));
      location = { pathname: parsedUrl.pathname, search: parsedUrl.search, hash: parsedUrl.hash };
    }
  }
  navigateFunction?.(location, { replace: true, ...options });
}
/**
 * Used only when navigating within the case/draft entity hierarchy using deep linking to child entities
 */
export function toChildItem(route: To, sectionId: number, replaceCurrent: boolean = true, parentRoute: string = '') {
  const querySection = new URLSearchParams(location?.search).get('section');
  const routeArg = {
    pathname: parentRoute + route,
    search: stringifyQuery({ ...queryObject, section: !!sectionId ? String(sectionId) : querySection })
  } as Location;
  const options = { state: { internal: true } };
  replaceCurrent ? replace(routeArg, options) : to(routeArg, options);
}

export function toMetroLineSection(route: To) {
  const routeArg = { pathname: route, querySingleton: queryObject } as unknown as Location<any>;
  const options = { state: { internal: true } };
  replace(routeArg, options);
}

export function isRouteInPath(path: string, route: string) {
  const reg = new RegExp(`^\/?${route}`);
  const search = path.search(reg);

  return search === 0;
}

export const stringifyQuery = (query: QueryObject) => query && '?' + Object.keys(query)
  .map((key) => {
    const queryKey: string | string[] = query[key];
    if (Array.isArray(queryKey)) {
      return queryKey
        .map((param: string) => `${encodeURIComponent(key)}=${encodeURIComponent(param)}`)
        .join('&');
    }

    return `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`;
  })
  .join('&');

export function toSelectCaseTypes(keepSearchParams: boolean = true) {
  const { pathname } = currentLocation;
  keepSearchParams ? replace({ pathname, search: stringifyQuery({ ...(queryObject) }) }) :
    replace({ pathname: routeConstants.selectCaseType });
}

export function toCodeMappingsList(id: number, internal: boolean = true) {
  to({ pathname: routeConstants.codeMappingsFor(id) }, { state: { internal } });
}

export function toImportMappingsList(importSetId?: number, codetableId?: number, internal: boolean = true) {
  to({
    pathname: (isDefinedIncludingZero(importSetId) && isDefinedIncludingZero(codetableId))
      ? routeConstants.importMappingsEditFor(importSetId, codetableId)
      : (isDefinedIncludingZero(importSetId) && !isDefinedIncludingZero(codetableId))
        ? routeConstants.importMappingsImportSetOnlyFor(importSetId)
        : routeConstants.importMappings
  }, { state: { internal } });
}

export function toAddExternalUrl() {
  window.open(toAbsoluteUrl(routeConstants.externalUrlAddFor()));
}

export function toManageExternalUrl(favouriteId: number) {
  window.open(toAbsoluteUrl(routeConstants.externalUrlEditFor(favouriteId)));
}

export function toUserMembers(subscriberGroupId: number) {
  window.open(toAbsoluteUrl(routeConstants.subscriberGroupsUserMembersFor(subscriberGroupId)));
}

export function toAdminSubscriberGroupList(internal: boolean = true, newWindow: boolean = false) {
  newWindow 
    ? window.open(toAbsoluteUrl(routeConstants.subscriberGroupsList))
    : to({ pathname: routeConstants.subscriberGroupsList }, { state: { internal } });
}

export function toOperations() {
  window.open(toAbsoluteUrl(routeConstants.experienceTransferOperations));
}

export function toCaseSaveAsDraftView(draftNumber: number, entityPath: string = '') {
  const searchParams = new URLSearchParams(location?.search);
  if (!searchParams.has('saveAsDraft')) {
    searchParams.set('saveAsDraft', 'true');
  }
  replace({ pathname: routeUrls.toDraft(draftNumber, entityPath), search: searchParams?.toString() });
}
